import { useMemo, useRef, useState } from 'react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { HideContainer } from '@components/hide-container';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropRight } from '@components/icons/dropright';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconIntegration } from '@components/icons/integration';
import { IconList } from '@components/icons/list';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconRestart } from '@components/icons/restart';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconTextShort } from '@components/icons/text-short';
import { InfoBox } from '@components/info-box';
import { LabelTextGroup } from '@components/label-text-group';
import { SizedRelativeSpinner, Spinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';

import { useCreateSyncList, usePatchSyncList, useListSyncTestRequest } from './list-hooks';
import {
  buildArrayLocationPayload,
  buildEndpointUrl,
  buildMappingLabelDefaultValue,
  buildPaginatedUrl,
  createLabelFromKey,
  dotToBracketNotation,
  extractUrlPlaceholders,
  extractValueFromPath,
  filterSingleValueMappingKeys,
  flattenObjectToPaths,
  formatArrayLocationLabel,
  getMappingKeysForArrayLocation,
  getPathFromEndpointUrl,
  hasMultipleArrayLocations,
  isNestedArrayLocation,
  substituteParametersIntoUrl,
} from './list-utils';

import {
  Conditions,
  useConditionState,
  processConditionsForSubmission,
  ConditionType,
} from '../condition';
import {
  useIntegrationList,
  useLists,
  useListsScope,
  ListsScopeProvider,
} from '../integrations-hooks';
import type {
  List,
  ListSyncTestRequestType,
  SyncListCreateData,
  SyncListConditions,
  SyncListParameter,
  SortOrder,
} from '../integrations-types';

type SyncListFormType = {
  name: string;
  integration?: string;
  url?: string;
  path?: string;
  mappingLabel: VariableValue;
  mappingValue: string;
  conditionType?: ConditionType;
  sortOrder?: SortOrder;
  paginationTokenPath?: string;
  paginationParamName?: string;
  // Per-URL-placeholder fields are registered dynamically — see parameterLabelFieldId / parameterTestValueFieldId.
  [parameterField: `parameter-${string}-label`]: string;
  [parameterField: `parameter-${string}-test-value`]: string;
};

const parameterLabelFieldId = (name: string) => `parameter-${name}-label` as const;
const parameterTestValueFieldId = (name: string) => `parameter-${name}-test-value` as const;

const Form = createForm<SyncListFormType>();

export const IntegrationThumbnail = ({ thumbnail }: { thumbnail?: string | null }) => {
  if (thumbnail) {
    return <SquareImage src={thumbnail} />;
  }
  return <IconIntegration color="elementPurpleInvert" />;
};

export type SyncListSidebarProps = {
  list?: List;
  baseUrl?: string; // For Marketplace: use path-based URL building
  integrationId?: string; // For Marketplace: pre-selected integration ID
  templateId?: string; // For Marketplace: template context for API calls
  connectionVariableNames?: string[]; // URL placeholders matching these are filled at connect time, not by the list.
};

export const SyncListSidebar = ({
  list,
  baseUrl,
  integrationId,
  templateId,
  connectionVariableNames,
}: SyncListSidebarProps) => {
  return (
    <ListsScopeProvider templateId={templateId}>
      <SyncListSidebarContent
        list={list}
        baseUrl={baseUrl}
        integrationId={integrationId}
        connectionVariableNames={connectionVariableNames}
      />
    </ListsScopeProvider>
  );
};

const SyncListSidebarContent = ({
  list,
  baseUrl,
  integrationId: defaultIntegrationId,
  connectionVariableNames,
}: Omit<SyncListSidebarProps, 'templateId'>) => {
  const { templateId: scopedTemplateId } = useListsScope();
  const allowDynamicList = Boolean(scopedTemplateId);
  const showIntegrationSelect = !defaultIntegrationId;
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const importingModal = useSyncListImportingModal();

  const listsQuery = useLists();
  const integrationsQuery = useIntegrationList();
  const createMutation = useCreateSyncList();
  const editMutation = usePatchSyncList();

  const listName = formStore.useFieldValue('name') as string;
  const integration = formStore.useFieldValue('integration') as string;
  const url = formStore.useFieldValue('url') as string;
  const path = formStore.useFieldValue('path') as string;
  const paginationTokenPath = formStore.useFieldValue('paginationTokenPath') as string;
  const paginationParamName = formStore.useFieldValue('paginationParamName') as string;

  const currentIntegrationId = integration || defaultIntegrationId || '';
  const testMutation = useListSyncTestRequest(currentIntegrationId);

  const [responseStatus, setResponseStatus] = useState<'initial' | 'success' | 'error'>(
    list ? 'success' : 'initial'
  );
  const [testResponse, setTestResponse] = useState<ListSyncTestRequestType | null>(null);
  const [selectedArrayLocation, setSelectedArrayLocation] = useState<string | undefined>(
    list?.sync?.arrayLocation
  );
  const isEdit = Boolean(list);

  const showInitialMappingRef = useRef(false);
  const testRequestModal = useSyncListTestRequestModal();
  const formValues = formStore.useFormValues();

  const initialConditions = useMemo(
    () => list?.sync?.conditions?.conditions ?? [],
    [list?.sync?.conditions?.conditions]
  );
  const {
    conditions,
    setConditions,
    conditionsChanged,
    handleAddCondition,
    handleRemoveCondition,
  } = useConditionState(initialConditions);

  const getConditionsForSubmission = (): SyncListConditions | null => {
    if (conditions.length === 0) {
      return null;
    }
    const processedConditions = processConditionsForSubmission(conditions, formValues);
    const conditionType = (formValues.conditionType as 'all' | 'any') ?? 'all';
    return {
      conditions: processedConditions,
      conditionType,
    };
  };

  const integrationOptions = useMemo(() => {
    if (!integrationsQuery.data) return [];
    return integrationsQuery.data
      .filter((int) => int.authentication?.isAuthenticated)
      .map((int) => ({
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
              <IntegrationThumbnail thumbnail={int.thumbnail} />
            </div>
            {int.name}
          </div>
        ),
        value: int.shortId,
        filterText: int.name,
      }));
  }, [integrationsQuery.data]);

  const existingListNames = useMemo(() => {
    return listsQuery.data?.map((l) => l.name) ?? [];
  }, [listsQuery.data]);

  const testUrl = useMemo(() => {
    if (baseUrl && path !== undefined) {
      return buildEndpointUrl(baseUrl, path ?? '');
    }
    return url ?? '';
  }, [baseUrl, path, url]);

  const parameterNames = useMemo(() => {
    if (!allowDynamicList) return [];
    const connectionVars = new Set(connectionVariableNames ?? []);
    return extractUrlPlaceholders(testUrl).filter((name) => !connectionVars.has(name));
  }, [testUrl, connectionVariableNames, allowDynamicList]);

  const isDynamicList = parameterNames.length > 0;

  const parameters: SyncListParameter[] = useMemo(
    () =>
      parameterNames.map((name) => {
        const previous = list?.sync?.parameters?.find((parameter) => parameter.name === name);
        return {
          name,
          label: (formValues[parameterLabelFieldId(name)] as string) ?? previous?.label ?? name,
          testValue:
            (formValues[parameterTestValueFieldId(name)] as string) ?? previous?.testValue ?? '',
        };
      }),
    [parameterNames, formValues, list?.sync?.parameters]
  );

  const allParameterTestValuesFilled = parameters.every(
    (parameter) => parameter.testValue.trim() !== ''
  );

  const paginationPathOptions = useMemo(() => {
    if (!testResponse?.exampleData) return [];
    return flattenObjectToPaths(testResponse.exampleData)
      .filter((p) => p.type !== 'object' && p.type !== 'array')
      .map((p) => ({ value: p.path, label: dotToBracketNotation(p.path) }));
  }, [testResponse?.exampleData]);

  const paginationPreviewUrl = useMemo(() => {
    if (!paginationTokenPath || !paginationParamName || !testResponse?.exampleData || !testUrl)
      return undefined;
    const value = extractValueFromPath(testResponse.exampleData, paginationTokenPath);
    if (value === undefined) return undefined;
    return buildPaginatedUrl(testUrl, paginationParamName, String(value));
  }, [paginationTokenPath, paginationParamName, testResponse?.exampleData, testUrl]);

  const handleTestEndpoint = async () => {
    if (baseUrl) {
      changePath(path);
    } else {
      changeSource(integration, url);
    }
    const urlToTest = isDynamicList
      ? substituteParametersIntoUrl(
          testUrl,
          Object.fromEntries(parameters.map(({ name, testValue }) => [name, testValue]))
        )
      : testUrl;
    try {
      const response = await testMutation.mutateAsync({ url: urlToTest });
      setTestResponse(response);
      if (response.arrayLocations && response.arrayLocations.length > 0) {
        const previousPickStillValid = response.arrayLocations.some(
          (option) => option.name === selectedArrayLocation
        );
        if (!previousPickStillValid) {
          setSelectedArrayLocation(undefined);
        }
      } else {
        setSelectedArrayLocation(undefined);
      }
      testRequestModal({ response });
      setResponseStatus(response.success ? 'success' : 'error');
    } catch {
      setResponseStatus('error');
    }
  };

  const showArrayLocationPicker = hasMultipleArrayLocations(testResponse);
  // The merge only applies to a nested path, and that can be auto-selected
  // without the picker ever showing.
  const showNestedArrayMergeHelp = isNestedArrayLocation(selectedArrayLocation, testResponse);
  const mappingKeys = useMemo(() => {
    return getMappingKeysForArrayLocation(
      testResponse,
      selectedArrayLocation,
      list?.sync?.mappingKeys
    );
  }, [testResponse, selectedArrayLocation, list?.sync?.mappingKeys]);
  // Label + value options hold a single value: an option is a { label, value }
  // pair, so array/object keys are never valid targets. Conditions keep the full set.
  const options = useMemo(() => {
    return filterSingleValueMappingKeys(mappingKeys).map((key) => ({
      label: key.name,
      value: key.name,
      type: key.type,
    }));
  }, [mappingKeys]);

  const arrayLocationOptions = useMemo(() => {
    return (testResponse?.arrayLocations ?? []).map((option) => ({
      value: option.name,
      label: formatArrayLocationLabel(option.name),
    }));
  }, [testResponse?.arrayLocations]);

  const handleArrayLocationChange = (value: string) => {
    setSelectedArrayLocation(value);
    showInitialMappingRef.current = true;
  };

  const variableGroups: FieldMappingGroup[] = useMemo(
    () => [
      {
        id: 'list',
        label: 'List',
        fields: options.map((option) => ({
          label: option.label,
          value: option.value,
          name: option.value,
          source: option.value,
          field: option.type,
          type: option.type as FieldType,
        })),
        icon: <IconTextShort />,
      },
    ],
    [options]
  );

  // Conditions need 'payload_' prefix for backend resolution. Unlike the
  // label/value pickers, conditions keep the full key set (incl. arrays).
  const conditionVariableGroups: FieldMappingGroup[] = useMemo(
    () => [
      {
        id: 'payload',
        label: 'Payload',
        fields: mappingKeys.map((key) => ({
          label: key.name,
          value: key.name,
          name: key.name,
          source: 'payload',
          field: key.name,
          type: key.type as FieldType,
        })),
        icon: <IconTextShort />,
      },
    ],
    [mappingKeys]
  );

  const defaultMappingLabel = useMemo(() => {
    const fields = variableGroups.length > 0 ? variableGroups[0].fields : [];
    return buildMappingLabelDefaultValue(list?.sync?.mappingLabel ?? '', fields);
  }, [list?.sync?.mappingLabel, variableGroups]);

  const changeSource = (integrationValue: string, urlValue: string) => {
    if (
      integrationValue === list?.sync?.integration.shortId &&
      urlValue === list?.sync?.endpointUrl
    ) {
      setResponseStatus('success');
      showInitialMappingRef.current = false;
    } else {
      setResponseStatus('initial');
      showInitialMappingRef.current = true;
    }
  };

  const changePath = (pathValue: string) => {
    const defaultPath = getPathFromEndpointUrl(baseUrl ?? '', list?.sync?.endpointUrl);
    if (pathValue === defaultPath) {
      setResponseStatus('success');
      showInitialMappingRef.current = false;
    } else {
      setResponseStatus('initial');
      showInitialMappingRef.current = true;
    }
  };

  const handleCreateList = async (formData: SyncListFormType) => {
    const data: SyncListCreateData = {
      name: formData.name,
      integrationId: formData.integration ?? defaultIntegrationId ?? '',
      endpointUrl: testUrl,
      mappingLabel: formData.mappingLabel.value,
      mappingValue: formData.mappingValue,
      isActive: true,
      conditions: getConditionsForSubmission(),
      sortOrder: formData.sortOrder,
      paginationTokenPath: formData.paginationTokenPath || null,
      paginationParamName: formData.paginationParamName || null,
      parameters: isDynamicList ? parameters : undefined,
      ...(buildArrayLocationPayload(selectedArrayLocation, testResponse) ?? {}),
    };

    if (isEdit && list?.sync) {
      await editMutation.mutateAsync({ id: list.sync.shortId, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    hideSidebar();
    if (!isDynamicList) {
      importingModal();
    }
  };

  const hasChanges = isDirty || conditionsChanged;

  const duplicateListName = existingListNames.includes(listName) && listName !== list?.name;

  const responseStatusMap: Record<
    'initial' | 'success' | 'error',
    { Icon: React.ComponentType<{ color?: Color; size?: number }>; text: string; color: Color }
  > = {
    initial: {
      Icon: IconFormIndicatorInfo,
      text: t('testNotPerformed'),
      color: 'elementIndigo100',
    },
    success: { Icon: IconFormIndicatorSuccess, text: t('testSuccess'), color: 'elementGreen100' },
    error: {
      Icon: IconFormIndicatorError,
      text: testResponse?.error ?? t('testError'),
      color: 'elementRed100',
    },
  };

  const { Icon, text, color } = responseStatusMap[responseStatus];

  const defaultPath = baseUrl
    ? getPathFromEndpointUrl(baseUrl, list?.sync?.endpointUrl)
    : undefined;
  const canTest =
    (baseUrl ? Boolean(path) : Boolean(integration && url)) &&
    (!isDynamicList || allParameterTestValuesFilled);
  const hasPagination = Boolean(list?.sync?.paginationTokenPath || list?.sync?.paginationParamName);

  if (showIntegrationSelect && !integrationsQuery.data) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader
          icon={IconList}
          iconColor="elementBlue100"
          title={isEdit ? t('editSyncList') : t('addSyncList')}
        />
        <Sidebar.Content>
          <Spinner />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconList}
        iconColor="elementBlue100"
        title={isEdit ? t('editSyncList') : t('addSyncList')}
      />
      <Form store={formStore} onSubmit={handleCreateList}>
        <Sidebar.Content>
          <Stack space={4}>
            <HideContainer title={t('listDetails')}>
              <Stack space={4}>
                <Stack space={1}>
                  <Form.TextField
                    id="name"
                    placeholder={t('addListName')}
                    label={t('listName')}
                    autoFocus
                    validators={['required']}
                    defaultValue={list?.name}
                  />
                  {duplicateListName && <FormError text={t('duplicateListTitle')} />}
                </Stack>
                <Form.Select
                  id="sortOrder"
                  label={t('sorting')}
                  options={[
                    { value: 'api_order', label: t('apiOrder') },
                    { value: 'alphabetical', label: t('alphabetical') },
                  ]}
                  defaultValue={list?.sync?.sortOrder ?? 'api_order'}
                  isClearable={false}
                />
              </Stack>
            </HideContainer>
            <Separator />
            <HideContainer title={t('source')}>
              <Stack space={4}>
                {showIntegrationSelect && (
                  <>
                    <Text text={t('sourceInfo')} font="regular14" />
                    <Form.Select
                      id="integration"
                      isClearable={false}
                      label={t('sourceIntegration')}
                      options={integrationOptions}
                      validators={['required']}
                      onChange={(value) => changeSource(value, url)}
                      defaultValue={list?.sync?.integration.shortId}
                      hasFilterText
                    />
                  </>
                )}
                {baseUrl ? (
                  <Stack space={1}>
                    <Form.TextField
                      id="path"
                      placeholder={`${t('addPath')}...`}
                      label={t('path')}
                      validators={['required']}
                      onChange={(value) => changePath(value)}
                      defaultValue={defaultPath}
                    />
                    <Text font="regular12" text={`${baseUrl}${path ?? ''}`} color="textHelper" />
                  </Stack>
                ) : (
                  <Form.TextField
                    id="url"
                    placeholder={`${t('addUrl')}...`}
                    label={t('sourceUrl')}
                    validators={['required', 'valid-http-url']}
                    onChange={(value) => changeSource(integration, value)}
                    defaultValue={list?.sync?.endpointUrl}
                  />
                )}
              </Stack>
            </HideContainer>
            {isDynamicList && (
              <>
                <Separator />
                <HideContainer title={t('parameters')}>
                  <Stack space={4}>
                    <Text text={t('parametersInfo')} font="regular14" color="textHelper" />
                    {parameterNames.map((name) => {
                      const previous = list?.sync?.parameters?.find((p) => p.name === name);
                      return (
                        <Card key={name} space={4} variant="gray">
                          <Stack space={3}>
                            <LabelTextGroup label={t('parameterName')} text={name} />
                            <Form.TextField
                              id={parameterLabelFieldId(name)}
                              label={t('parameterLabel')}
                              defaultValue={previous?.label ?? createLabelFromKey(name)}
                            />
                            <Form.TextField
                              id={parameterTestValueFieldId(name)}
                              label={t('parameterTestValue')}
                              defaultValue={previous?.testValue ?? ''}
                            />
                          </Stack>
                        </Card>
                      );
                    })}
                  </Stack>
                </HideContainer>
              </>
            )}
            <Separator />
            <HideContainer title={t('testResponse')}>
              <Stack space={4}>
                <Text text={t('testResponseInfo')} font="regular14" />
                <Stack space={4} orientation="horizontal" align="center">
                  <Button
                    iconLeft={responseStatus === 'success' ? <IconRestart /> : <IconSend />}
                    isDisabled={!canTest || testMutation.isLoading}
                    text={responseStatus === 'success' ? t('reTest') : t('test')}
                    variant={responseStatus === 'success' ? 'secondary' : 'primary-blue'}
                    onClick={() => handleTestEndpoint()}
                  />
                  {testMutation.isLoading ? (
                    <SizedRelativeSpinner size={24} />
                  ) : (
                    <Stack space={2} orientation="horizontal" align="center">
                      <Icon color={color} size={16} />
                      <Text text={text} font="regular14" color={color} />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </HideContainer>
            <Separator />
            <HideContainer title={t('mapFields')}>
              {responseStatus !== 'success' ? (
                <Card space={6} variant="gray">
                  <Stack space={4} orientation="horizontal">
                    <IconMappingOff />
                    <Text text={t('mapFieldsInfo')} font="semibold12" />
                  </Stack>
                </Card>
              ) : (
                <Stack space={4}>
                  {showArrayLocationPicker && (
                    <Stack space={1}>
                      <Select
                        label={t('arrayLocationLabel')}
                        options={arrayLocationOptions}
                        value={selectedArrayLocation}
                        onChange={handleArrayLocationChange}
                        isClearable={false}
                        placeholder={t('arrayLocationPlaceholder')}
                      />
                      <Text text={t('arrayLocationHelp')} font="regular12" color="textHelper" />
                    </Stack>
                  )}
                  {showNestedArrayMergeHelp && (
                    <InfoBox variant="indigo" text={t('nestedArrayMergeHelp')} />
                  )}
                  <Form.FieldMappingTextEditor
                    id="mappingLabel"
                    key={`mapping-label-${responseStatus}-${selectedArrayLocation ?? ''}`}
                    label={t('label')}
                    singleLine
                    plaintext
                    fieldMappingId={`synced-list-${list?.sync?.shortId ?? 'new'}`}
                    variableGroups={variableGroups}
                    data-testid="label-field"
                    validators={['required']}
                    defaultValue={!showInitialMappingRef.current ? defaultMappingLabel : undefined}
                  />
                  <Form.Select
                    id="mappingValue"
                    key={`mapping-value-${responseStatus}-${selectedArrayLocation ?? ''}`}
                    label={t('value')}
                    options={options ?? []}
                    validators={['required']}
                    defaultValue={
                      !showInitialMappingRef.current ? list?.sync?.mappingValue : undefined
                    }
                  />
                </Stack>
              )}
            </HideContainer>
            {responseStatus === 'success' && (
              <>
                <Separator />
                <HideContainer title={t('addConditions')}>
                  <Conditions
                    conditions={conditions}
                    conditionType={list?.sync?.conditions?.conditionType}
                    variableGroups={conditionVariableGroups}
                    fieldMappingId={`synced-list-conditions-${list?.sync?.shortId ?? 'new'}`}
                    onAddCondition={handleAddCondition}
                    onRemoveCondition={handleRemoveCondition}
                    setConditions={setConditions}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    Form={Form as any}
                    helpText={t('conditionHelptext')}
                  />
                </HideContainer>
                <Separator />
                <HideContainer title={t('pagination')} defaultClosed={!hasPagination}>
                  <Stack space={4}>
                    <Text text={t('paginationInfo')} font="regular14" color="textHelper" />
                    {paginationPathOptions.length > 0 ? (
                      <Form.Select
                        id="paginationTokenPath"
                        label={t('tokenPath')}
                        options={paginationPathOptions}
                        defaultValue={list?.sync?.paginationTokenPath}
                        placeholder={t('selectTokenPath')}
                      />
                    ) : list?.sync?.paginationTokenPath ? (
                      <LabelTextGroup
                        label={t('tokenPath')}
                        text={`${dotToBracketNotation(list.sync.paginationTokenPath)} (${t(
                          'retestToChange'
                        )})`}
                      />
                    ) : (
                      <Text text={t('testToSelectPath')} font="regular12" color="textHelper" />
                    )}
                    <Form.TextField
                      id="paginationParamName"
                      label={t('paramName')}
                      defaultValue={list?.sync?.paginationParamName || undefined}
                    />
                    {paginationPreviewUrl && (
                      <LabelTextGroup label={t('nextUrl')} text={paginationPreviewUrl} />
                    )}
                    <PaginationLearnMore />
                  </Stack>
                </HideContainer>
              </>
            )}
          </Stack>
        </Sidebar.Content>
        <Sidebar.Footer>
          <Form.Actions>
            <ButtonGroup align="right">
              <Button onClick={hideSidebar} text={t('core.cancel')} variant="secondary" />
              <Form.Submit variant="primary-blue" text={t('save')} isDisabled={!hasChanges} />
            </ButtonGroup>
          </Form.Actions>
        </Sidebar.Footer>
      </Form>
    </Sidebar>
  );
};

const useSyncListTestRequestModal = () =>
  useModal(
    (props: { response: ListSyncTestRequestType }) => (
      <SyncListTestRequest response={props.response} />
    ),
    { variant: 'LARGE' }
  );

type TestModalProps = {
  response: ListSyncTestRequestType;
};

const SyncListTestRequest = ({ response }: TestModalProps) => {
  const { t } = useScopedTranslate(locale);
  const { exampleData, statusCode, success } = response;
  return (
    <Modal>
      <Modal.Header>
        <Text font="heading1" text={t('testResponse')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={4}>
          <Stack orientation="horizontal" align="center">
            <IconStatusIndicator color={success ? 'elementGreen200' : 'elementRed100'} />
            <Text
              font="regular14"
              color={success ? 'elementGreen200' : 'elementRed100'}
              text={`${success ? t('success') : t('fail')} • ${statusCode}`}
            />
          </Stack>
          <CodeBlock data={exampleData} hideFooter />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const useSyncListImportingModal = () => useModal(SyncListImporting, { variant: 'LARGE' });

const SyncListImporting = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Modal>
      <Modal.Header>
        <Text font="heading1" text={t('importing')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={1}>
          <Text text={t('syncProcessed')} font="bold16" />
          <Text text={t('syncTime')} />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

export const useSyncListSidebar = () => {
  return useSidebar(SyncListSidebar, {
    variant: 'REGULAR',
    closeOnClickOutside: true,
  });
};

export const PaginationLearnMore = () => {
  const { t } = useScopedTranslate(locale);
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Stack space={2}>
      <Stack
        space={1}
        orientation="horizontal"
        align="center"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        {isOpen ? (
          <IconDropdown size={16} color="elementIndigo100" />
        ) : (
          <IconDropRight size={16} color="elementIndigo100" />
        )}
        <Text text={t('learnMore')} font="regular12" color="elementIndigo100" />
      </Stack>
      {isOpen && <Text text={t('paginationExample')} font="regular12" color="textHelper" />}
    </Stack>
  );
};

const locale = {
  en: {
    addSyncList: 'Add sync list',
    editSyncList: 'Edit sync list',
    listDetails: 'List details',
    listName: 'List name',
    addListName: 'Add list name',
    source: 'Source',
    sourceInfo:
      'Select the third-party integration that you will use as your primary source for this list',
    sourceIntegration: 'Source integration',
    sourceUrl: 'Source URL',
    addUrl: 'Add url',
    path: 'Path',
    addPath: 'Add path',
    testResponse: 'Test response',
    testResponseInfo: 'Checks that the list response is correct.',
    test: 'Test',
    testNotPerformed: 'Test not performed',
    testSuccess: 'Test successful',
    testError: 'Test failed',
    reTest: 'Re-test',
    mapFields: 'Map fields',
    mapFieldsInfo: 'A valid test response has to be received before fields can be mapped',
    label: 'Label',
    value: 'Value',
    save: 'Save',
    duplicateListTitle: 'List with this name already exists',
    success: 'Success',
    fail: 'Fail',
    importing: 'Importing',
    syncProcessed: 'Sync is being processed in the background',
    syncTime:
      'Please note this can take some time depending on how many list options are being added',
    addConditions: 'Add conditions',
    conditionHelptext: 'Only include items that match the following conditions',
    sorting: 'Sorting',
    apiOrder: 'Default (as received)',
    alphabetical: 'Alphabetical',
    pagination: 'Pagination (optional)',
    paginationInfo:
      'Use this if your API returns multiple pages but does not include a "next page" URL.',
    learnMore: 'Learn more',
    paginationExample:
      'Token path: Where to find the next page value in the response (e.g. next_cursor)\n\nQuery parameter: The parameter used to request the next page (e.g. cursor)\n\nExample: If the response includes "next_cursor": "abc123" and the next request uses ?cursor=abc123, then Token path = next_cursor and Query param = cursor',
    tokenPath: 'Token path in response',
    paramName: 'Query parameter name',
    selectTokenPath: 'Select token path',
    retestToChange: 're-test to change',
    testToSelectPath: 'Test endpoint to select token path',
    nextUrl: 'Next page URL',
    parameters: 'Parameters',
    parametersInfo:
      'Parts of this URL are filled in each time the list loads. Add a test value for each so you can check the response.',
    parameterName: 'Parameter',
    parameterLabel: 'Label',
    parameterTestValue: 'Test value',
    arrayLocationLabel: 'Array',
    arrayLocationPlaceholder: 'Pick an array',
    arrayLocationHelp: 'The response has more than one array. Pick the one to sync.',
    nestedArrayMergeHelp:
      'This array is nested, so an item appearing under more than one parent is merged into a single option.',
  },
};
